import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Copy, AlertTriangle, ExternalLink } from 'lucide-react';

const APIKey = () => {
  const { api } = useAPI();

  const { data: APIData } = useQuery({
    queryFn: api.retrieve('user', 'current/api_key/'),
    queryKey: ['user/current/api_key/'],
  });

  const apiKey = APIData?.api_key;

  const copyAPIKey = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success(Locales.apikey.toast.success);
  };

  if (!APIData) {
    return null; // Or a loading state
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-balance text-2xl font-bold text-slate-900">{Locales.apikey.title}</h1>
            <p className="text-pretty text-sm text-slate-600">Gérez vos clés d&lsquo;accès à l&lsquo;API eHadj</p>
          </div>
        </div>

        {/* API Key Card */}
        <Card className="shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">{Locales.apikey.access_key}</CardTitle>
            <CardDescription className="text-pretty leading-relaxed">
              {Locales.apikey.about}
              <a
                href={`${process.env.REACT_APP_API_BASE_URL}doc`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-emerald-600 underline-offset-2 hover:underline"
              >
                {Locales.apikey.doc}
                <ExternalLink className="size-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-0">
            <Alert variant="destructive" className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{Locales.apikey.alert}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <label htmlFor="apiKey" className="text-sm font-medium text-slate-700">
                {Locales.base.field.access_key}
              </label>
              <div className="flex gap-2">
                <Input id="apiKey" type="text" value={apiKey} readOnly className="font-mono text-sm" />
                <Button onClick={copyAPIKey} variant="outline" size="icon" className="shrink-0 transition-all">
                  <Copy className="size-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">{Locales.apikey.helptext}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default APIKey;
